import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Heading,
	If,
	makeClassName,
	Row,
	StyleProps,
	toLocalDateString,
} from "@hex-insights/core";
import { useActivePageRegistration } from "@hex-insights/router";
import { ContractInstallmentUtilities, ContractUtilities, ModelUtilities } from "../../../.../../../../Utilities";
import { EmptyView } from "../../../../../../Components/Empty";
import { Icon } from "../../../../../../Components/Icon";
import { Tile } from "../../../../../../Components/Tile";
import { Timeline } from "../../../../../../Components/Timeline";
import {
	ContractListQueryVariables,
	ContractOrderField,
	OrderDirection,
	Student,
	StudentDetailQuery,
	TimelineFilterInput,
	TimelineObjectType,
	useContractListQuery,
} from "../../../../../../Utilities";
import { PaymentForm } from "../../../../../Forms";
import {
	ContractCreateModalController,
	ContractDueSummaryTile,
	ContractListSection,
	ContractPaymentProgressBar,
	ContractPaymentProgressText,
} from "../../../../Contract";
import {
	ContractInstallmentsPaymentStatusSummaryButtonGrid,
	ContractInstallmentsSummaryAccordion,
} from "../../../../ContractInstallment";
import { PaymentCreateModalController } from "../../../../Payment";
import { DetailRouteContext } from "../../RouteContext";

export type FinancialsBodyProps = {
	student: StudentDetailQuery["student"];
};

export function FinancialsBody({ student }: FinancialsBodyProps) {
	const { subPageInfos } = React.useContext(DetailRouteContext);
	useActivePageRegistration(subPageInfos.financials!);

	const { loading, data } = useContractListQuery({
		variables: getContractListQueryVariables(student.id),
	});

	const initialCreateFormValues = React.useMemo(() => ({ studentID: student.id }), [student.id]);

	if (loading && !data) {
		return <div>Loading...</div>;
	}

	if (!data || data.contractConnection.edges.length === 0) {
		return (
			<Row justify="spaced-start" style={{ flexGrow: 1 }}>
				<EmptyView IconComponent={Icon.Money} primaryText="Financials" secondaryText="No financial records listed">
					<ContractCreateModalController variant="link" size="small" initialFormValues={initialCreateFormValues}>
						Click to Add a Contract
					</ContractCreateModalController>
				</EmptyView>
			</Row>
		);
	}

	return (
		<Row justify="spaced-start" style={{ flexGrow: 1 }}>
			<Column justify="spaced-start" style={{ width: "25rem", flexShrink: 0 }}>
				<SummarySection student={student} />
				<ContractsSection student={student} />
			</Column>
			<Column justify="spaced-start" style={{ flexGrow: 1, minWidth: 0 }}>
				<PaymentProgressSection student={student} />
				<MainContentSection student={student} style={{ flexGrow: 1 }} />
			</Column>
		</Row>
	);
}

function getContractListQueryVariables(
	studentID: Student["id"],
	status?: ContractUtilities.ContractStatus,
): ContractListQueryVariables {
	const filters: NonNullable<ContractListQueryVariables["filters"]> = { studentIDEQ: studentID };
	if (status === ContractUtilities.ContractStatus.Active) {
		filters.isFinalizedEQ = true;
		filters.dateVoidedIsNull = true;
	} else if (status === ContractUtilities.ContractStatus.Void) {
		filters.dateVoidedIsNull = false;
	} else if (status === ContractUtilities.ContractStatus.Draft) {
		filters.isFinalizedEQ = false;
		filters.dateVoidedIsNull = true;
	}

	return {
		filters,
		order: { field: ContractOrderField.EffectiveDate, direction: OrderDirection.Desc },
	};
}

type PageSectionProps = {
	student: StudentDetailQuery["student"];
} & Partial<ClassNameProps & StyleProps>;

function SummarySection({ student, className, style }: PageSectionProps) {
	const { loading, data } = useContractListQuery({
		variables: getContractListQueryVariables(student.id, ContractUtilities.ContractStatus.Active),
	});

	return (
		<ContractDueSummaryTile
			variant="admin"
			studentID={student.id}
			headingLevel={3}
			isLoading={loading}
			contracts={data?.contractConnection.edges}
			className={className}
			style={style}
		/>
	);
}

function ContractsSection({ student, className, style }: PageSectionProps) {
	const { loading, data } = useContractListQuery({
		variables: getContractListQueryVariables(student.id),
	});

	const initialCreateFormValues = React.useMemo(() => ({ studentID: student.id }), [student.id]);

	return (
		<ContractListSection
			variant="admin"
			isLoading={loading}
			contracts={data?.contractConnection.edges}
			Container={Tile}
			headingLevel={3}
			initialCreateFormValues={initialCreateFormValues}
			emptyView="full"
			className={className}
			style={style}
		/>
	);
}

function PaymentProgressSection({ student, className, style }: PageSectionProps) {
	const { data } = useContractListQuery({
		variables: getContractListQueryVariables(student.id, ContractUtilities.ContractStatus.Active),
	});
	const paymentProgress = React.useMemo(() => {
		if (!data) {
			return ContractUtilities.blankPaymentProgressInformation;
		}
		const paymentProgresses = data.contractConnection.edges.map(({ node }) =>
			ContractUtilities.calculatePaymentProgress(node),
		);
		return ContractUtilities.mergePaymentProgresses(paymentProgresses);
	}, [data]);

	const contractInstallmentGroups = React.useMemo(() => {
		if (!data) {
			return {
				[ModelUtilities.PaymentStatus.Paid]: [],
				[ModelUtilities.PaymentStatus.Overdue]: [],
				[ModelUtilities.PaymentStatus.Due]: [],
				[ModelUtilities.PaymentStatus.Upcoming]: [],
			};
		}

		const precomputedContractInstallments = ContractUtilities.precomputeContractInstallments(
			data.contractConnection.edges,
		);
		const contractInstallmentGroups = ContractInstallmentUtilities.groupContractInstallmentsByPaymentStatus(
			precomputedContractInstallments,
		);
		return contractInstallmentGroups;
	}, [data]);

	const paymentCreateFormStateArgs = React.useMemo<PaymentForm.ControlledCreateProps["formStateArgs"]>(() => {
		return {
			initialFormValues: {
				issueDate: toLocalDateString(new Date()),
			},
		};
	}, []);

	return (
		<Tile className={className} style={style}>
			<Tile.Header>
				<Row justify="space-between">
					<Heading.H3 noMargin>Payment Progress</Heading.H3>

					<PaymentCreateModalController
						variant="tertiary"
						studentID={student.id}
						formStateArgs={paymentCreateFormStateArgs}
						className="button--primary-tertiary"
						style={{ fontWeight: "bold" }}
					>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<Icon.PlusCircle size={16} /> Record Payment
						</Row>
					</PaymentCreateModalController>
				</Row>
			</Tile.Header>
			<Tile.Body>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<Column justify="spaced-start" verticalSpacing="0.5rem">
						<ContractPaymentProgressBar paymentProgress={paymentProgress} />
						<ContractPaymentProgressText paymentProgress={paymentProgress} />
					</Column>

					<ContractInstallmentsPaymentStatusSummaryButtonGrid
						variant="admin"
						studentID={student.id}
						paidContractInstallments={contractInstallmentGroups[ModelUtilities.PaymentStatus.Paid]}
						overdueContractInstallments={contractInstallmentGroups[ModelUtilities.PaymentStatus.Overdue]}
						dueContractInstallments={contractInstallmentGroups[ModelUtilities.PaymentStatus.Due]}
						upcomingContractInstallments={contractInstallmentGroups[ModelUtilities.PaymentStatus.Upcoming]}
					/>
				</Column>
			</Tile.Body>
		</Tile>
	);
}

function MainContentSection({ student, className, style }: PageSectionProps) {
	const [view, setView] = React.useState<"payment-schedule" | "timeline">("payment-schedule");

	const { data } = useContractListQuery({
		variables: getContractListQueryVariables(student.id, ContractUtilities.ContractStatus.Active),
	});

	const precomputedContractInstallments = React.useMemo(() => {
		if (!data) {
			return [];
		}
		return ContractUtilities.precomputeContractInstallments(data.contractConnection.edges);
	}, [data]);

	const timelineFilter = React.useMemo<TimelineFilterInput>(
		() => ({
			objectTypeIn: [TimelineObjectType.AcademicTerm, TimelineObjectType.Contract, TimelineObjectType.Payment],
			studentID: student.id,
		}),
		[student.id],
	);

	return (
		<Tile className={className} style={style}>
			<Tile.Header>
				<Row justify="spaced-start">
					<Button
						onClick={() => setView("payment-schedule")}
						className={makeClassName("button--nav", view === "payment-schedule" ? "button--nav--active" : "")}
						style={{ fontSize: "1rem", fontWeight: "bold" }}
					>
						Payment Schedule
					</Button>
					<Button
						onClick={() => setView("timeline")}
						className={makeClassName("button--nav", view === "timeline" ? "button--nav--active" : "")}
						style={{ fontSize: "1rem", fontWeight: "bold" }}
					>
						Timeline
					</Button>
				</Row>
			</Tile.Header>
			<Tile.Body>
				<Conditional>
					<If condition={view === "payment-schedule"}>
						<ContractInstallmentsSummaryAccordion
							variant="admin"
							contractInstallments={precomputedContractInstallments}
						/>
					</If>
					<If condition={view === "timeline"}>
						<Timeline filter={timelineFilter} pageSize={10} style={{ height: "100%" }} />
					</If>
				</Conditional>
			</Tile.Body>
		</Tile>
	);
}
